import React, { useState } from "react";
import { useForm } from "react-hook-form";
import Modal from "../../UI/Modal";
import TextInput from "../../UI/TextInput";
import Selection from "../../UI/Selection";
import { Button } from "../../UI/Button";
import type {
  CategoryDto,
  ProductLightDto,
  DuplicateProductRequest,
} from "../../../../domain/types/company/company-product-system";

interface DuplicateProductForm {
  name: string;
}

interface DuplicateProductModalProps {
  sourceProduct: ProductLightDto;
  sourceCategoryId: number;
  categories: CategoryDto[];
  onClose: () => void;
  onSubmit: (request: DuplicateProductRequest) => Promise<void>;
}

export const DuplicateProductModal: React.FC<DuplicateProductModalProps> = ({
  sourceProduct,
  sourceCategoryId,
  categories,
  onClose,
  onSubmit,
}) => {
  const form = useForm<DuplicateProductForm>({
    shouldUnregister: false,
    defaultValues: { name: `${sourceProduct.name} (사본)` },
  });
  const [selectedCategoryId, setSelectedCategoryId] = useState(
    String(sourceCategoryId),
  );
  const [submitting, setSubmitting] = useState(false);

  const handleFormSubmit = async (data: DuplicateProductForm) => {
    if (!data.name.trim()) return;
    try {
      setSubmitting(true);
      await onSubmit({
        name: data.name.trim(),
        targetCategoryId: Number(selectedCategoryId),
      });
    } catch {
      alert("상품 복제에 실패했습니다.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal setIsModalVisible={onClose} title="같은 상품으로 복제하기">
      <div className="w-[70vw] max-w-[28rem]" />
      <form
        className="flex-col overflow-hidden text-[14px]"
        onSubmit={form.handleSubmit(handleFormSubmit)}
      >
        <div className="mb-4">
          <label className="block text-[13px] font-bold text-[#555] mb-2">
            카테고리
          </label>
          <div className="h-[42px]">
            <Selection
              selectedOption={selectedCategoryId}
              setSelectedOption={setSelectedCategoryId}
              options={categories.map((category) => ({
                value: String(category.id),
                name: category.name,
              }))}
            />
          </div>
        </div>

        <div className="mb-5">
          <label className="block text-[13px] font-bold text-[#555] mb-2">
            새 상품 이름
          </label>
          <TextInput
            error={{
              message: form.formState.errors.name?.message ?? "",
              isMessageVisible: true,
              isIconVisible: true,
            }}
            {...form.register("name", {
              required: { value: true, message: "필수 항목이에요" },
            })}
            maxLength={100}
            placeholder="예: 현수막(3.2M장폭)"
            autoFocus
          />
        </div>

        <p className="text-xs text-[#888] mb-5">
          복제된 상품은 비활성 상태로 추가됩니다. 내용을 검토한 뒤 상세
          페이지에서 활성화해주세요.
        </p>

        <div className="flex justify-end gap-[0.75rem]">
          <Button type="button" variant="outline" onClick={onClose}>
            취소
          </Button>
          <Button variant="primary" type="submit" disabled={submitting}>
            {submitting ? "복제 중..." : "복제하기"}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
